/* Collection Family Sections — enhancement over server-rendered sections.
 *
 * The sections themselves are fully server-rendered by Liquid
 * (blocks/collection-product-grid.liquid, .cfs markup). This script adds:
 *
 *   1. LAZY CARD HYDRATION: each family renders its first 8 products as full
 *      cards; products 9+ ship as fixed-size placeholder shells whose real
 *      card markup sits inert inside a <template> (no images fetched, no
 *      layout cost). Batches of 8 hydrate on idle time after window load
 *      (requestIdleCallback, setTimeout fallback) AND proactively when the
 *      user scrolls the row near the unhydrated region (IntersectionObserver
 *      on the first placeholder, rooted to the row) — whichever fires first,
 *      until the family is fully hydrated. Placeholders share the cards'
 *      exact flex sizing, so the row's scrollWidth is honest from first
 *      paint and the scrubber never jumps.
 *   2. DRAG SCRUBBER under each row: slim track + proportional draggable
 *      handle mapped to the row's horizontal scroll (pointer capture, track
 *      click-to-jump, scroll/resize sync, arrow keys when focused). Hidden
 *      while the row doesn't overflow. Also owns the right fade-edge toggle.
 *   3. FAMILY DATA HYDRATION: one batched call to the colors backend
 *      (window.HealixColors.families(category, gender) — cached/shared) paints
 *      • the Core / Limited swatch STRIP in each family header — real 30px
 *        button swatches (PDP variant-picker styling); clicking one locates
 *        that colourway's card in the family row (force-hydrates lazy
 *        batches until the card exists, scrolls to it, flash-ring pulse;
 *        missing card → navigate to the colour's product URL), and
 *      • per-card COLOUR TAGS through the card's existing badge system: a
 *        colour-name chip (background = the colourway's hex, text colour by
 *        luminance) plus a "Limited Edition" image-overlay chip for LE
 *        colourways. Tags reuse the theme's tag/badge classes
 *        (snippets/tag.liquid + badge-list.liquid) and re-apply to newly
 *        hydrated cards after every lazy batch.
 *      No per-card or per-family fetches — one response powers everything.
 *
 * Without JS the first 8 cards per family render and scroll natively; the
 * placeholder shells are plain product links.
 */
(function () {
  'use strict';

  var BATCH = 8;
  var MODE_KEY = 'cfs:layout'; // 'scroll' (default) | 'grid'
  var GRID_CLASS = 'cfs-mode-grid'; // on <html>; early-boot script pre-applies it

  function isGridMode() {
    return document.documentElement.classList.contains(GRID_CLASS);
  }

  function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function handleFromUrl(url) {
    var m = /\/products\/([^/?#]+)/.exec(url || '');
    return m ? m[1] : '';
  }

  // ----- lazy hydration (batches of 8 from <template> placeholders) ------------

  function initLazy(section, onBatch) {
    var row = section.querySelector('[data-family-row]');
    if (!row) return;

    var io = null;
    var finished = false;

    function placeholders() {
      return row.querySelectorAll('[data-cfs-placeholder]');
    }

    // Swap one placeholder shell for its real card. Returns the card node.
    function hydrateOne(ph) {
      var tpl = ph.querySelector('template[data-cfs-card]');
      if (!tpl) {
        ph.remove();
        return null;
      }
      var frag = tpl.content.cloneNode(true);
      var card = frag.querySelector('[data-product-card]') || frag.firstElementChild;
      ph.replaceWith(frag);
      return card;
    }

    // Hydrate up to BATCH placeholders (DOM order). Returns count hydrated.
    function hydrateBatch() {
      var phs = placeholders();
      var n = Math.min(BATCH, phs.length);
      for (var i = 0; i < n; i++) hydrateOne(phs[i]);
      if (n > 0 && typeof onBatch === 'function') onBatch(section);
      if (placeholders().length === 0) finish();
      return n;
    }

    function finish() {
      if (finished) return;
      finished = true;
      if (io) io.disconnect();
    }

    // (a) idle-time chain, starting only after window load so hydration
    // never competes with initial render.
    function idleChain() {
      if (finished) return;
      var cb = function () {
        hydrateBatch();
        idleChain();
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(cb, { timeout: 3000 });
      } else {
        setTimeout(cb, 500);
      }
    }

    if (document.readyState === 'complete') {
      idleChain();
    } else {
      window.addEventListener('load', idleChain, { once: true });
    }

    // (b) proactive: when the row is scrolled near the unhydrated region.
    if ('IntersectionObserver' in window) {
      var observeNext = function () {
        io.disconnect();
        if (finished) return;
        var first = placeholders()[0];
        if (first) io.observe(first);
      };
      io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              hydrateBatch();
              observeNext();
              return;
            }
          }
        },
        { root: row, rootMargin: '0px 800px 0px 800px' }
      );
      observeNext();
    }

    // Synchronous force-hydration until a card with `handle` exists (used by
    // the swatch click-to-locate). Returns the card or null.
    section._cfsEnsureCard = function (handle) {
      var guard = 200;
      while (guard--) {
        var card = findCard(section, handle);
        if (card) return card;
        if (hydrateBatch() === 0) break;
      }
      return findCard(section, handle);
    };

    // Synchronous full hydration (grid mode "Show all").
    section._cfsHydrateAll = function () {
      var guard = 200;
      while (guard-- && hydrateBatch() > 0) {}
    };
  }

  // ----- layout mode (toolbar switcher): scroller rows <-> stacked grids ------
  //
  // The toolbar's two repurposed view buttons ([data-cfs-mode-toggle]) switch
  // every family section between the default horizontal scroller and a grid
  // (2 cols mobile / the theme's --grid-columns-* on desktop — pure CSS via
  // html.cfs-mode-grid, pre-applied before paint by an early-boot script).
  // In grid mode each family shows its first 8 products with a "Show all N"
  // button that force-hydrates the family's lazy batches and reveals the
  // rest. Show-all state resets when the mode changes.

  function gridProducts(section) {
    // Row children that count as products (video card is a bonus first cell).
    var row = sectionRow(section);
    if (!row) return [];
    return Array.prototype.filter.call(row.children, function (el) {
      return !el.classList.contains('cfs-video-card');
    });
  }

  // (Re)apply the collapsed state: products 9+ hidden unless expanded.
  // Runs after every lazy batch too, so newly hydrated cards stay hidden
  // while their family is collapsed.
  function applyGridCollapse(section) {
    var items = gridProducts(section);
    for (var i = 0; i < items.length; i++) {
      if (i >= BATCH && !section._cfsExpanded) {
        items[i].setAttribute('data-cfs-grid-hidden', '');
      } else {
        items[i].removeAttribute('data-cfs-grid-hidden');
      }
    }
    if (section._cfsShowAllBtn) {
      section._cfsShowAllBtn.hidden =
        section._cfsExpanded || items.length <= BATCH;
    }
  }

  function initGridCollapse(section) {
    section._cfsExpanded = false;

    // Expand: force-hydrate everything left (family only — color sections have
    // no lazy placeholders, all cards are server-rendered), then reveal.
    section._cfsShowAll = function () {
      if (section._cfsExpanded) return;
      section._cfsExpanded = true;
      if (section._cfsHydrateAll) section._cfsHydrateAll();
      applyGridCollapse(section);
    };

    var count = sectionCount(section);
    if (count > BATCH) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cfs-show-all';
      btn.setAttribute(
        'aria-label',
        'Show all ' + count + ' ' + sectionLabel(section)
      );
      btn.innerHTML =
        '<span>Show all ' + count + '</span>' +
        '<i class="ph-bold ph-caret-down" aria-hidden="true"></i>';
      btn.addEventListener('click', function () {
        section._cfsShowAll();
      });
      var wrap = section.querySelector('.cfs-row-wrap');
      if (wrap) wrap.insertAdjacentElement('afterend', btn);
      section._cfsShowAllBtn = btn;
    }

    applyGridCollapse(section);
  }

  function initModeToggle(sections) {
    var toggle = document.querySelector('[data-cfs-mode-toggle]');

    function applyMode(mode, persist) {
      mode = mode === 'grid' ? 'grid' : 'scroll';
      document.documentElement.classList.toggle(GRID_CLASS, mode === 'grid');
      if (toggle) {
        toggle.querySelectorAll('[data-cfs-mode]').forEach(function (b) {
          var active = b.getAttribute('data-cfs-mode') === mode;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
      // Show-all state resets on every switch; scrubbers re-sync when the
      // rows become scrollable again.
      sections.forEach(function (section) {
        section._cfsExpanded = false;
        applyGridCollapse(section);
        section.dispatchEvent(new CustomEvent('cfs:hydrated'));
      });
      if (persist) {
        try { localStorage.setItem(MODE_KEY, mode); } catch (e) {}
      }
    }

    // The early-boot script already put the class on <html> pre-paint; here we
    // just sync button state (and collapse state) to it.
    applyMode(isGridMode() ? 'grid' : 'scroll', false);

    if (toggle) {
      toggle.querySelectorAll('[data-cfs-mode]').forEach(function (b) {
        b.addEventListener('click', function () {
          applyMode(b.getAttribute('data-cfs-mode'), true);
        });
      });
    }
  }

  // ----- row controls: floating arrows + drag scrubber + fade edge ------------

  // The row element for a section — family sections use [data-family-row],
  // color-collection category sections use [data-color-row]. The scrubber /
  // grid-collapse / mode CSS all key off .cfs-row(-wrap), which both carry, so
  // resolving either attr lets those behaviours work unchanged for color
  // sections without any family hydration.
  function sectionRow(section) {
    return (
      section.querySelector('[data-family-row]') ||
      section.querySelector('[data-color-row]')
    );
  }

  // Human label for a section — family title, or the color category label.
  function sectionLabel(section) {
    return (
      section.getAttribute('data-family-title') ||
      section.getAttribute('data-color-label') ||
      'products'
    );
  }

  // Product count for grid-collapse — family count or color category count.
  function sectionCount(section) {
    return (
      parseInt(section.getAttribute('data-family-count'), 10) ||
      parseInt(section.getAttribute('data-color-count'), 10) ||
      0
    );
  }

  function initScrubber(section) {
    var row = sectionRow(section);
    var wrap = section.querySelector('.cfs-row-wrap');
    if (!row || !wrap) return;

    function makeArrow(direction) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'cfs-row-arrow cfs-row-arrow--' + direction;
      button.setAttribute(
        'aria-label',
        (direction === 'prev' ? 'Previous ' : 'Next ') + sectionLabel(section)
      );
      button.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="' + (direction === 'prev' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6') + '" />' +
        '</svg>';
      wrap.appendChild(button);
      return button;
    }

    var prev = makeArrow('prev');
    var next = makeArrow('next');

    var scrub = document.createElement('div');
    scrub.className = 'cfs-scrub';
    var track = document.createElement('div');
    track.className = 'cfs-scrub__track';
    var handle = document.createElement('div');
    handle.className = 'cfs-scrub__handle';
    handle.tabIndex = 0;
    handle.setAttribute('role', 'scrollbar');
    handle.setAttribute('aria-orientation', 'horizontal');
    handle.setAttribute(
      'aria-label',
      'Scroll ' + sectionLabel(section)
    );
    handle.setAttribute('aria-valuemin', '0');
    handle.setAttribute('aria-valuemax', '100');
    track.appendChild(handle);
    scrub.appendChild(track);
    wrap.insertAdjacentElement('afterend', scrub);

    function maxScroll() {
      return row.scrollWidth - row.clientWidth;
    }

    function arrowTop() {
      var media = row.querySelector(
        '.product-card__image-container, .cfs-video-card__media, .cfs-placeholder'
      );
      if (!media) return wrap.clientHeight * 0.5;
      var mediaRect = media.getBoundingClientRect();
      var wrapRect = wrap.getBoundingClientRect();
      return mediaRect.top - wrapRect.top + mediaRect.height * 0.5;
    }

    function itemStart(item) {
      var itemRect = item.getBoundingClientRect();
      var rowRect = row.getBoundingClientRect();
      return row.scrollLeft + itemRect.left - rowRect.left;
    }

    function scrollTarget(direction) {
      var current = row.scrollLeft;
      var starts = [];
      for (var i = 0; i < row.children.length; i++) {
        var item = row.children[i];
        if (!item.hidden && !item.hasAttribute('data-cfs-grid-hidden')) {
          starts.push(itemStart(item));
        }
      }
      if (direction > 0) {
        for (var nextIndex = 0; nextIndex < starts.length; nextIndex++) {
          if (starts[nextIndex] > current + 2) return starts[nextIndex];
        }
        return maxScroll();
      }
      for (var prevIndex = starts.length - 1; prevIndex >= 0; prevIndex--) {
        if (starts[prevIndex] < current - 2) return starts[prevIndex];
      }
      return 0;
    }

    function scrollRow(direction) {
      var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollTo({
        left: scrollTarget(direction),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    prev.addEventListener('click', function () { scrollRow(-1); });
    next.addEventListener('click', function () { scrollRow(1); });

    function sync() {
      var max = maxScroll();
      var atStart = row.scrollLeft <= 2;
      var atEnd = row.scrollLeft >= max - 2;
      wrap.toggleAttribute('data-at-end', atEnd);

      var controlsHidden = max <= 2 || isGridMode();
      var top = arrowTop() + 'px';
      prev.style.top = top;
      next.style.top = top;
      prev.hidden = controlsHidden || atStart;
      next.hidden = controlsHidden || atEnd;
      prev.disabled = controlsHidden || atStart;
      next.disabled = controlsHidden || atEnd;

      if (max <= 2) {
        scrub.hidden = true;
        return;
      }
      scrub.hidden = false;
      // Fixed-width pill (sized by CSS): position maps scroll progress
      // across (trackWidth - handleWidth).
      var x = (track.clientWidth - handle.offsetWidth) * (row.scrollLeft / max);
      handle.style.transform = 'translateX(' + Math.max(0, x) + 'px)';
      handle.setAttribute('aria-valuenow', String(Math.round((row.scrollLeft / max) * 100)));
    }

    // Drag: pointer capture on the handle; movement maps 1:1 track→scroll.
    var drag = null;
    handle.addEventListener('pointerdown', function (e) {
      drag = { x: e.clientX, left: row.scrollLeft };
      handle.setPointerCapture(e.pointerId);
      scrub.classList.add('is-dragging');
      row.classList.add('cfs-row--free'); // suspend scroll-snap while dragging
      e.preventDefault();
    });
    handle.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var range = track.clientWidth - handle.offsetWidth;
      var max = maxScroll();
      if (range <= 0 || max <= 0) return;
      row.scrollLeft = drag.left + (e.clientX - drag.x) * (max / range);
    });
    function endDrag() {
      drag = null;
      scrub.classList.remove('is-dragging');
      row.classList.remove('cfs-row--free');
    }
    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);

    // Click on the track (outside the handle) jumps there.
    scrub.addEventListener('pointerdown', function (e) {
      if (e.target === handle || drag) return;
      var rect = track.getBoundingClientRect();
      var range = rect.width - handle.offsetWidth;
      if (range <= 0) return;
      var ratio = (e.clientX - rect.left - handle.offsetWidth / 2) / range;
      ratio = Math.max(0, Math.min(1, ratio));
      row.scrollTo({ left: ratio * maxScroll(), behavior: 'smooth' });
    });

    // Keyboard: arrow keys page the row by half a viewport.
    handle.addEventListener('keydown', function (e) {
      var step = row.clientWidth * 0.5;
      if (e.key === 'ArrowRight') {
        row.scrollBy({ left: step, behavior: 'smooth' });
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        row.scrollBy({ left: -step, behavior: 'smooth' });
        e.preventDefault();
      }
    });

    row.addEventListener('scroll', sync, { passive: true });
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(sync);
      ro.observe(row);
      ro.observe(track);
    } else {
      window.addEventListener('resize', sync);
    }
    section.addEventListener('cfs:hydrated', sync);
    sync();
  }

  // ----- family video card (first row item where the family has a video) -------
  //
  // Hosted videos: server-rendered <video preload="none" muted loop
  // playsinline>. An IntersectionObserver plays it while ~half visible and
  // pauses it off-screen — a live showcase with zero cost until scrolled to.
  // The corner speaker button (Phosphor) toggles sound. External videos
  // (YouTube/Vimeo) can't autoplay-on-visible reliably, so they ship as
  // poster + play button and swap in their embed iframe on click.

  function initVideoCard(section) {
    var card = section.querySelector('[data-cfs-video]');
    if (!card) return;

    var video = card.querySelector('video');
    if (video) {
      video.muted = true; // belt-and-braces for autoplay policies

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                var p = video.play();
                if (p && p.catch) p.catch(function () {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.4 }
        ).observe(card);
      }

      var sound = card.querySelector('[data-cfs-video-sound]');
      if (sound) {
        var icon = sound.querySelector('i');
        sound.addEventListener('click', function (e) {
          e.stopPropagation(); // don't trigger the fullscreen click on the video
          video.muted = !video.muted;
          sound.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
          sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
          if (icon) {
            icon.classList.toggle('ph-speaker-simple-slash', video.muted);
            icon.classList.toggle('ph-speaker-simple-high', !video.muted);
          }
          if (!video.muted) {
            var p = video.play();
            if (p && p.catch) p.catch(function () {});
          }
        });
      }

      // Click-to-fullscreen on the video itself (native fullscreen API — it
      // letterboxes to the video's own aspect ratio automatically). While
      // fullscreen: unmuted + native controls; both restored on exit. iOS
      // Safari has no element fullscreen; webkitEnterFullscreen() opens the
      // native player (its own controls) and webkitendfullscreen restores.
      function syncSoundUi() {
        if (!sound) return;
        sound.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
        sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        var ic = sound.querySelector('i');
        if (ic) {
          ic.classList.toggle('ph-speaker-simple-slash', video.muted);
          ic.classList.toggle('ph-speaker-simple-high', !video.muted);
        }
      }

      var preFsMuted = true;
      var inFullscreen = false;

      function enterFsState() {
        inFullscreen = true;
        // Letterbox instead of the card's object-fit:cover crop (class
        // mirrors the :fullscreen CSS rule for engines that miss it).
        video.classList.add('is-fullscreen');
        video.muted = false;
        video.controls = true;
        syncSoundUi();
      }

      function onFsExit() {
        if (!inFullscreen) return;
        inFullscreen = false;
        video.classList.remove('is-fullscreen');
        video.muted = preFsMuted;
        video.controls = false;
        syncSoundUi();
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }

      video.addEventListener('click', function () {
        preFsMuted = video.muted;
        if (video.requestFullscreen) {
          video.requestFullscreen().then(enterFsState).catch(function () {});
        } else if (video.webkitRequestFullscreen) {
          video.webkitRequestFullscreen();
          enterFsState();
        } else if (video.webkitEnterFullscreen) {
          // iOS Safari: native fullscreen player (has its own controls)
          inFullscreen = true;
          video.muted = false;
          syncSoundUi();
          video.webkitEnterFullscreen();
        }
      });

      document.addEventListener('fullscreenchange', function () {
        if (document.fullscreenElement !== video) onFsExit();
      });
      document.addEventListener('webkitfullscreenchange', function () {
        if (document.webkitFullscreenElement !== video) onFsExit();
      });
      video.addEventListener('webkitendfullscreen', onFsExit);
      return;
    }

    // External video: click swaps poster/play for the embed iframe.
    var play = card.querySelector('[data-cfs-video-play]');
    if (play) {
      play.addEventListener('click', function () {
        var src = play.getAttribute('data-video-src');
        if (!src) return;
        var media = card.querySelector('.cfs-video-card__media');
        var iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', 'Product video');
        media.appendChild(iframe);
        play.remove();
        var poster = card.querySelector('.cfs-video-card__poster');
        if (poster) poster.remove();
      });
    }
  }

  // ----- header swatch strip: clickable colour picker for the family row -------
  //
  // Each swatch is a real <button> styled after the PDP variant picker
  // (.vpick__swatch: 30px circle, white padding gap, border that darkens on
  // hover/selection). Clicking one LOCATES that colourway's card in the
  // family row: if the card is still a lazy placeholder it's force-hydrated
  // first (batches swap in until it exists), then the row scrolls to it with
  // a short flash-ring pulse. The swatch keeps an active ring until another
  // swatch in the family is chosen. If the card isn't in the family at all
  // we navigate to the colour's product URL instead.

  function findCard(section, handle) {
    if (!handle) return null;
    return section.querySelector(
      '[data-family-row] [data-product-card][data-product-handle="' + handle + '"]'
    );
  }

  function flashCard(card) {
    var scope = card.closest('[data-family-sections]') || document;
    scope.querySelectorAll('.cfs-card-flash').forEach(function (el) {
      el.classList.remove('cfs-card-flash');
    });
    void card.offsetWidth; // restart the animation if re-clicked
    card.classList.add('cfs-card-flash');
    card.addEventListener('animationend', function onEnd() {
      card.classList.remove('cfs-card-flash');
      card.removeEventListener('animationend', onEnd);
    });
  }

  function locateCard(section, handle, url) {
    var card = findCard(section, handle);
    if (!card && section._cfsEnsureCard) {
      card = section._cfsEnsureCard(handle);
    }
    if (!card) {
      if (url) window.location.href = url;
      return;
    }
    if (isGridMode()) {
      // Grid mode: the card may sit beyond the collapsed 8 — expand the
      // family first, then scroll the PAGE vertically to it.
      if (card.hasAttribute('data-cfs-grid-hidden') && section._cfsShowAll) {
        section._cfsShowAll();
      }
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      flashCard(card);
      return;
    }
    var row = section.querySelector('[data-family-row]');
    if (!row) return;
    var delta = card.getBoundingClientRect().left - row.getBoundingClientRect().left;
    row.scrollBy({ left: delta - 12, behavior: 'smooth' });
    flashCard(card);
  }

  function makeSwatch(section, color, editionLabel) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cfs-swatch';
    var name = color.name || color.slug || 'Colour';
    btn.setAttribute('aria-label', name + ' — ' + editionLabel);
    btn.title = name;
    if (color.slug) btn.setAttribute('data-color-slug', color.slug);

    var chip = document.createElement('span');
    chip.className = 'cfs-swatch__chip';
    if (color.hex) chip.style.backgroundColor = color.hex;
    btn.appendChild(chip);

    btn.addEventListener('click', function () {
      section.querySelectorAll('.cfs-swatch.is-active').forEach(function (el) {
        if (el !== btn) el.classList.remove('is-active');
      });
      btn.classList.add('is-active');
      locateCard(section, handleFromUrl(color.url), color.url);
    });

    return btn;
  }

  function renderSwatchRows(section, colors) {
    var host = section.querySelector('[data-family-swatches]');
    if (!host) return;
    host.textContent = '';
    host.setAttribute('role', 'group');
    host.setAttribute(
      'aria-label',
      (section.getAttribute('data-family-title') || 'Family') + ' colours'
    );

    var groups = [
      { label: 'Core', items: [] },
      { label: 'Limited', items: [] }
    ];
    var seen = {};
    colors.forEach(function (c) {
      if (!c || !c.slug || seen[c.slug]) return;
      seen[c.slug] = true;
      (c.edition === 'core' ? groups[0] : groups[1]).items.push(c);
    });

    var any = false;
    groups.forEach(function (g) {
      if (!g.items.length) return;
      any = true;
      var groupEl = document.createElement('div');
      groupEl.className = 'cfs-swatches__group';
      var label = document.createElement('span');
      label.className = 'cfs-swatches__label';
      label.textContent = g.label;
      groupEl.appendChild(label);
      var dots = document.createElement('div');
      dots.className = 'cfs-swatches__dots';
      g.items.forEach(function (c) {
        dots.appendChild(makeSwatch(section, c, g.label));
      });
      groupEl.appendChild(dots);
      host.appendChild(groupEl);
    });

    host.hidden = !any;
  }

  // Perceived luminance (WCAG-ish) — decide white vs dark tag text on the hex.
  function tagTextColor(hex) {
    var h = String(hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return '#FFFFFF';
    var r = parseInt(h.slice(0, 2), 16) / 255;
    var g = parseInt(h.slice(2, 4), 16) / 255;
    var b = parseInt(h.slice(4, 6), 16) / 255;
    var lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return lum > 0.6 ? '#1D2129' : '#FFFFFF';
  }

  // Build one chip using the theme tag component's classes (snippets/tag.liquid):
  // .tag.tag--mini.tag--solid + .product-card__badge (badge-list sizing hook).
  function makeTag(text, hex, slug) {
    var tag = document.createElement('span');
    tag.className = 'tag tag--mini tag--solid product-card__badge' + (hex ? ' tag--hex' : ' tag--default');
    if (hex) {
      tag.style.setProperty('--tag-hex', hex);
      tag.style.setProperty('--tag-hex-text', tagTextColor(hex));
    }
    if (slug) tag.setAttribute('data-color-slug', slug);
    var txt = document.createElement('span');
    txt.className = 'tag__text';
    txt.textContent = text;
    tag.appendChild(txt);
    return tag;
  }

  // Mount point for our tags: the SAME containers badge-list.liquid renders
  // into, but always a dedicated (non-rotating) .product-card__badges div so
  // we never fight the badge-rotation script. Standard cards: inside
  // .product-card__info-row1 (created when the card had no badges/rating);
  // other card styles: an image-overlay container on the card root.
  function tagContainer(card) {
    var existing = card.querySelector('.product-card__badges.pc-color-tags');
    if (existing) return existing;

    var box = document.createElement('div');
    box.className = 'product-card__badges pc-color-tags';

    var info = card.querySelector('.product-card__info--below');
    if (info) {
      var row1 = info.querySelector('.product-card__info-row1');
      if (!row1) {
        row1 = document.createElement('div');
        row1.className = 'product-card__info-row1';
        info.insertBefore(row1, info.firstChild);
      }
      var spacer = row1.querySelector('.product-card__info-row1-spacer');
      var badgesRow = row1.querySelector('.product-card__badges-row');
      if (!badgesRow) {
        badgesRow = document.createElement('div');
        badgesRow.className = 'product-card__badges-row';
        if (spacer) row1.replaceChild(badgesRow, spacer);
        else row1.insertBefore(badgesRow, row1.firstChild);
      }
      badgesRow.appendChild(box);
    } else {
      // Non-standard card styles: overlay on the image (the image container
      // is the card's positioning context).
      var media = card.querySelector('.product-card__image-container') || card;
      media.appendChild(box);
    }
    return box;
  }

  function hydrateCardTags(section, colors) {
    var byHandle = {};
    colors.forEach(function (c) {
      if (!c || !c.url) return;
      var m = /\/products\/([^/?#]+)/.exec(c.url);
      if (m) byHandle[m[1]] = c;
    });

    section.querySelectorAll('[data-product-card]').forEach(function (card) {
      if (card.querySelector('.pc-color-tags')) return; // already hydrated
      var handle = card.getAttribute('data-product-handle') || '';
      var color = byHandle[handle];
      if (!color || !color.name) return; // card outside family data: no tag

      var box = tagContainer(card);
      box.appendChild(makeTag(color.name, color.hex || '', color.slug || ''));
      if (color.edition && color.edition !== 'core') {
        // Limited badge: TOP-LEFT image overlay so it reads at a glance.
        // (Old position — next to the color tag in the badges row — revert by
        // replacing the two lines below with: box.appendChild(limited);)
        var limited = makeTag('Limited', '', '');
        limited.classList.add('pc-limited-overlay');
        var media = card.querySelector('.product-card__image-container');
        (media || box).appendChild(limited);
      }
    });
  }

  // COLOR MODE — single-color collection card badges.
  // Color pages have no family/swatch data, but every product on the page is the
  // SAME color, so each card should still get the color chip that family cards
  // get. Reuses makeTag + tagContainer (and the Limited overlay) exactly like
  // hydrateCardTags, driven off the color matched from HealixColors.colors().
  //
  // colorRoot carries data-color-handle + data-color-gender (server-rendered).
  // The color is identified by matching that handle against each colors() entry's
  // collections[gender] — the SAME match the color switcher uses.
  function findColorForCollection(list, gender, handle) {
    if (!list) return null;
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (c && c.collections && c.collections[gender] === handle) return c;
    }
    return null;
  }

  function hydrateColorCards(colorRoot, color) {
    if (!color) return;
    colorRoot.querySelectorAll('[data-product-card]').forEach(function (card) {
      if (card.querySelector('.pc-color-tags')) return; // guard: don't double-add
      var box = tagContainer(card);
      box.appendChild(makeTag(color.name, color.hex || '', color.slug || ''));
      if (color.edition && color.edition !== 'core') {
        var limited = makeTag('Limited', '', '');
        limited.classList.add('pc-limited-overlay');
        var media = card.querySelector('.product-card__image-container');
        (media || box).appendChild(limited);
      }
    });
  }

  function hydrateColorCollection(colorRoot) {
    if (!window.HealixColors || typeof window.HealixColors.colors !== 'function') return;
    var handle = colorRoot.getAttribute('data-color-handle') || '';
    var gender = (colorRoot.getAttribute('data-color-gender') || 'women').trim().toLowerCase();
    if (gender !== 'women' && gender !== 'men') gender = 'women';
    if (!handle) return;

    function start() {
      window.HealixColors.colors().then(function (list) {
        var color = findColorForCollection(list || [], gender, handle);
        try { hydrateColorCards(colorRoot, color); } catch (e) {}
      }).catch(function () {});
    }
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2000 });
    else setTimeout(start, 200);
  }

  // Sections carry their own data-family-category (Shop All spans several
  // categories), so hydration groups sections per category and issues one
  // families() call each — the shared HealixColors cache dedupes with any
  // other consumer. Calls start on idle after load so they never compete
  // with first paint. The accessories group has no family mapping and is
  // skipped (no swatch strip / colour tags there).
  function hydrateFamilies(root, sections) {
    if (!window.HealixColors || !window.HealixColors.families) return;
    var gender = root.getAttribute('data-family-gender');
    if (!gender) return;

    var byCategory = {};
    sections.forEach(function (section) {
      var cat = section.getAttribute('data-family-category');
      if (!cat || cat === 'accessories') return;
      (byCategory[cat] = byCategory[cat] || []).push(section);
    });

    function hydrateCategory(cat) {
      window.HealixColors.families(cat, gender).then(function (families) {
        if (!families || !families.length) return;
        var bySlug = {};
        var byTitle = {};
        families.forEach(function (f) {
          if (f.slug) bySlug[f.slug] = f;
          if (f.title) byTitle[normalize(f.title)] = f;
        });
        byCategory[cat].forEach(function (section) {
          // Title before slug — slug is only the first title word (e.g. both
          // "Sleek Eight-pocket…" and "Sleek Eight-Pocket… TT" map to "sleek").
          var fam =
            byTitle[normalize(section.getAttribute('data-family-title'))] ||
            bySlug[section.getAttribute('data-family-slug')];
          if (!fam || !fam.colors || !fam.colors.length) return;
          section._cfsColors = fam.colors; // re-applied after each lazy batch
          renderSwatchRows(section, fam.colors);
          hydrateCardTags(section, fam.colors);
        });
      });
    }

    function start() {
      Object.keys(byCategory).forEach(hydrateCategory);
    }
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2000 });
    else setTimeout(start, 200);
  }

  // ----- init -------------------------------------------------------------------

  function init() {
    var familyRoot = document.querySelector('[data-family-sections]');
    var colorRoot = document.querySelector('[data-color-collection]');
    if (!familyRoot && !colorRoot) return;

    var familySections = familyRoot
      ? Array.prototype.slice.call(familyRoot.querySelectorAll('[data-family-section]'))
      : [];
    // Color-collection category sections get the LAYOUT behaviour only (mode
    // toggle + scrubber + grid-collapse) — no lazy hydration, no video card,
    // and crucially NO hydrateFamilies/hydrateCardTags (single-color pages have
    // no family/swatch data). Their cards are all server-rendered real cards.
    var colorSections = colorRoot
      ? Array.prototype.slice.call(colorRoot.querySelectorAll('[data-color-section]'))
      : [];

    function onBatch(section) {
      // Newly hydrated cards need their colour tags, the scrubber needs a
      // proportions re-sync, and grid mode needs the collapse re-applied so
      // hydrated cards beyond 8 stay hidden until Show all.
      if (section._cfsColors) hydrateCardTags(section, section._cfsColors);
      applyGridCollapse(section);
      section.dispatchEvent(new CustomEvent('cfs:hydrated'));
    }

    familySections.forEach(function (section) {
      initLazy(section, onBatch);
      initScrubber(section);
      initVideoCard(section);
      initGridCollapse(section);
    });

    // Color sections: shared layout wiring, no hydration path.
    colorSections.forEach(function (section) {
      initScrubber(section);
      initGridCollapse(section);
    });

    // One shared toolbar drives every section (family + color) — single source
    // of truth for the scroll/grid mode + html.cfs-mode-grid + localStorage.
    initModeToggle(familySections.concat(colorSections));

    // Family data hydration only runs against a real family root.
    if (familyRoot) hydrateFamilies(familyRoot, familySections);

    // Color-collection card badges: every product is the collection's single
    // color, so stamp that one color's chip onto each server-rendered card by
    // reusing makeTag/tagContainer (mirrors hydrateCardTags). One pass — color
    // cards are all server-rendered, no lazy batches to re-apply after.
    if (colorRoot) hydrateColorCollection(colorRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
